import { API_URL } from '../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Facebook, Instagram, Plus, Eye, UserPlus, Phone, Mail, MapPin,
  CheckCircle, Clock, XCircle, RefreshCw, Trash2, MessageSquare,
  TrendingUp, Users, Target, Calendar, Download, Send, PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const MetaLeads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [newLead, setNewLead] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    campaign_name: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const statusParam = filterStatus !== 'all' ? `&status=${filterStatus}` : '';
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/meta/leads?limit=100${statusParam}`),
        axios.get(`${API_URL}/api/meta/leads/stats`)
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/meta/leads/${leadId}`, { status: newStatus });
      toast.success('Durum güncellendi ve Meta\'ya bildirildi');
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleContactToggle = async (leadId, isContacted) => {
    try {
      await axios.put(`${API_URL}/api/meta/leads/${leadId}`, { 
        is_contacted: !isContacted,
        status: !isContacted ? 'contacted' : 'new'
      });
      toast.success(isContacted ? 'İletişim kaldırıldı' : 'İletişim kuruldu - Meta\'ya bildirildi');
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleSyncFromMeta = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API_URL}/api/meta/leads/sync`);
      toast.success(`${response.data.synced} yeni lead senkronize edildi`);
      fetchData();
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Meta senkronizasyonu başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateNotes = async (leadId, notes) => {
    try {
      await axios.put(`${API_URL}/api/meta/leads/${leadId}`, { contact_notes: notes });
      toast.success('Notlar kaydedildi');
      fetchData();
    } catch (error) {
      toast.error('Not kaydetme başarısız');
    }
  };

  const handleMarkQualified = async (leadId) => {
    try {
      await axios.put(`${API_URL}/api/meta/leads/${leadId}`, { status: 'qualified' });
      toast.success('Lead "Nitelikli" olarak işaretlendi ve Meta\'ya bildirildi');
      fetchData();
    } catch (error) {
      toast.error('İşaretleme başarısız');
    }
  };

  const handleMarkLost = async (leadId) => {
    if (!window.confirm('Bu lead\'i "Kayıp" olarak işaretlemek istediğinize emin misiniz?')) return;
    try {
      await axios.put(`${API_URL}/api/meta/leads/${leadId}`, { status: 'lost' });
      toast.success('Lead "Kayıp" olarak işaretlendi');
      fetchData();
    } catch (error) {
      toast.error('İşaretleme başarısız');
    }
  };

  const handleConvertToCustomer = async (leadId) => {
    try {
      await axios.post(`${API_URL}/api/meta/leads/${leadId}/convert`);
      toast.success('Lead müşteriye dönüştürüldü');
      fetchData();
    } catch (error) {
      toast.error('Dönüştürme başarısız');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Bu lead\'i silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/meta/leads/${leadId}`);
      toast.success('Lead silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    if (!newLead.full_name || !newLead.phone) {
      toast.error('Ad ve telefon zorunludur');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/meta/leads/manual`, newLead);
      toast.success('Lead eklendi');
      setIsAddOpen(false);
      setNewLead({ full_name: '', email: '', phone: '', city: '', campaign_name: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Ekleme başarısız');
    }
  };

  const getStatusBadge = (status, isContacted, isConverted) => {
    if (isConverted) return <Badge className="bg-green-500">Müşteri Oldu</Badge>;
    if (status === 'lost') return <Badge variant="secondary" className="bg-gray-400">Kayıp</Badge>;
    if (status === 'qualified') return <Badge className="bg-purple-500">Nitelikli</Badge>;
    if (status === 'contacted' || isContacted) return <Badge className="bg-blue-500">İletişim Kuruldu</Badge>;
    if (status === 'new') return <Badge variant="destructive">Yeni</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'instagram') return <Instagram className="h-4 w-4 text-pink-500" />;
    return <Facebook className="h-4 w-4 text-blue-600" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="meta-leads-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Facebook className="h-6 w-6 text-blue-600" />
            Meta Leads
          </h1>
          <p className="text-muted-foreground">Facebook ve Instagram reklamlarından gelen potansiyel müşteriler</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button 
            variant="default" 
            onClick={handleSyncFromMeta} 
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Download className={`h-4 w-4 mr-2 ${syncing ? 'animate-bounce' : ''}`} />
            {syncing ? 'Senkronize Ediliyor...' : 'Meta\'dan Çek'}
          </Button>
          <Button variant="outline" onClick={() => setIsAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Manuel Ekle
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Toplam Lead</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <Target className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Yeni</p>
                  <p className="text-2xl font-bold">{stats.new}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <Phone className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">İletişim Kuruldu</p>
                  <p className="text-2xl font-bold">{stats.contacted}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Müşteri Oldu</p>
                  <p className="text-2xl font-bold">{stats.converted}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Calendar className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bu Hafta</p>
                  <p className="text-2xl font-bold">{stats.this_week}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <Label>Filtrele:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="new">Yeni</SelectItem>
                <SelectItem value="contacted">İletişim Kuruldu</SelectItem>
                <SelectItem value="converted">Müşteri Oldu</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Listesi</CardTitle>
          <CardDescription>
            Meta reklamlarından gelen tüm potansiyel müşteriler
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Facebook className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Henüz lead bulunmuyor</p>
              <p className="text-sm mt-2">Meta reklamlarından gelen leadler burada görünecek</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>İletişim</TableHead>
                  <TableHead>Kampanya</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      {getPlatformIcon(lead.platform)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{lead.full_name || '-'}</p>
                        {lead.city && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {lead.city}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`} className="text-sm flex items-center gap-1 text-blue-600 hover:underline">
                            <Phone className="h-3 w-3" />
                            {lead.phone}
                          </a>
                        )}
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} className="text-xs flex items-center gap-1 text-muted-foreground hover:underline">
                            <Mail className="h-3 w-3" />
                            {lead.email}
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{lead.campaign_name || '-'}</p>
                      {lead.ad_name && (
                        <p className="text-xs text-muted-foreground">{lead.ad_name}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(lead.status, lead.is_contacted, lead.is_converted)}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{formatDate(lead.created_at)}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}
                          title="Detay"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!lead.is_converted && lead.status !== 'lost' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleContactToggle(lead.id, lead.is_contacted)}
                              title={lead.is_contacted ? "İletişim Kaldır" : "Aradım"}
                              className={lead.is_contacted ? "text-blue-600" : ""}
                            >
                              <PhoneCall className="h-4 w-4" />
                            </Button>
                            {lead.is_contacted && lead.status !== 'qualified' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkQualified(lead.id)}
                                title="Nitelikli İşaretle"
                                className="text-purple-600"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleConvertToCustomer(lead.id)}
                              title="Müşteriye Dönüştür"
                              className="text-green-600"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkLost(lead.id)}
                              title="Kayıp İşaretle"
                              className="text-gray-400"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(lead.id)}
                          className="text-red-500"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Lead Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLead && getPlatformIcon(selectedLead.platform)}
              Lead Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Ad Soyad</Label>
                  <p className="font-medium">{selectedLead.full_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Telefon</Label>
                  <p className="font-medium">{selectedLead.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">E-posta</Label>
                  <p className="font-medium">{selectedLead.email || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Şehir</Label>
                  <p className="font-medium">{selectedLead.city || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Kampanya Bilgileri</Label>
                <div className="mt-2 space-y-1">
                  <p><span className="text-muted-foreground">Kampanya:</span> {selectedLead.campaign_name || '-'}</p>
                  <p><span className="text-muted-foreground">Reklam Seti:</span> {selectedLead.adset_name || '-'}</p>
                  <p><span className="text-muted-foreground">Reklam:</span> {selectedLead.ad_name || '-'}</p>
                  <p><span className="text-muted-foreground">Platform:</span> {selectedLead.platform || 'facebook'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Tarih</Label>
                <p>{formatDate(selectedLead.created_at)}</p>
              </div>
              
              {selectedLead.field_data && selectedLead.field_data.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Form Verileri</Label>
                  <div className="mt-2 bg-slate-50 dark:bg-slate-900 p-3 rounded-lg text-sm">
                    {selectedLead.field_data.map((field, idx) => (
                      <p key={idx}>
                        <span className="text-muted-foreground">{field.name}:</span> {field.values?.join(', ')}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>Kapat</Button>
            {selectedLead && !selectedLead.is_converted && (
              <Button onClick={() => { handleConvertToCustomer(selectedLead.id); setIsDetailOpen(false); }}>
                <UserPlus className="h-4 w-4 mr-2" />
                Müşteriye Dönüştür
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manuel Lead Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ad Soyad *</Label>
                <Input
                  value={newLead.full_name}
                  onChange={(e) => setNewLead({ ...newLead, full_name: e.target.value })}
                  placeholder="Ad Soyad"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon *</Label>
                <Input
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  placeholder="05XX XXX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label>E-posta</Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Şehir</Label>
                <Input
                  value={newLead.city}
                  onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                  placeholder="Şehir"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Kampanya Adı</Label>
              <Input
                value={newLead.campaign_name}
                onChange={(e) => setNewLead({ ...newLead, campaign_name: e.target.value })}
                placeholder="Kampanya adı (opsiyonel)"
              />
            </div>
            <div className="space-y-2">
              <Label>Notlar</Label>
              <Input
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                placeholder="Ek notlar..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>İptal</Button>
              <Button type="submit">Ekle</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetaLeads;
